import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ProjetsService } from 'app/services/projets/projets.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';

interface Project {
  id: number;
  name: string;
  createdAt: string;
  photos: string[];
  agencyName: string;
  status: string;
  pieces: any[];
  displayOrder?: number;
  description?: string;
  adresse?: string;
  date?: string;
  categoryList?: string[];
  videoUrlProjet?: string;
  nomCommercial?: string;
  numCommercial?: string;
  emailCommercial?: string;
  promoTitle?: string;
  promoImg?: string;
  projetLink?: string;
}

interface SortOption {
  value: string;
  label: string;
  icon: string;
}

@Component({
  selector: 'app-projects-list',
  templateUrl: './projects-list.component.html',
  styleUrls: ['./projects-list.component.scss']
})
export class ProjectsListComponent implements OnInit {
  idAgency: any;
  Agency: any;
  list: Project[] = [];
  searchTerm: string = '';
  isLoading = false;
  projectsLoaded = false;
  piecesLoading = true;

  currentPage: number = 1;
  itemsPerPage: number = 8;
  totalPages: number = 0;
  paginatedProjects: Project[] = [];

  sortBy: string = 'order';
  sortDirection: 'asc' | 'desc' = 'asc';
  sortOptions: SortOption[] = [
    { value: 'order', label: 'Ordre', icon: 'sort' },
    { value: 'name', label: 'Nom (A-Z)', icon: 'sort_by_alpha' },
    { value: 'date', label: 'Date', icon: 'date_range' },
    { value: 'apartments', label: 'Appartements', icon: 'meeting_room' }
  ];

  constructor(
    private agencieService: AgenciesService,
    private activatedRoute: ActivatedRoute,
    private projetService: ProjetsService,
    private awsService: AwscontrolService,  // <-- injected
    private location: Location,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.idAgency = this.activatedRoute.snapshot.paramMap.get('id');
    this.agencieService.setIdAgency(this.idAgency);
    this.loadProjects();
  }

  private parseDate(dateString: string): Date {
    const parts = dateString.split(/[\s/:]+/);
    if (parts.length >= 6) {
      const [day, month, year, hour, minute, second] = parts.map(Number);
      return new Date(year, month - 1, day, hour, minute, second);
    }
    return new Date(dateString);
  }

  loadProjects(): void {
    this.isLoading = true;
    this.piecesLoading = true;
    
    this.agencieService.getAgencieById(this.idAgency).subscribe(
      (data: any) => {
        this.Agency = data;
        const projects = this.Agency?.projets as Project[] || [];
        if (projects.length === 0) {
          this.list = [];
          this.isLoading = false;
          this.piecesLoading = false;
          this.projectsLoaded = true;
          return;
        }

        this.list = projects.map(p => ({ ...p, pieces: [] }));
        this.initializeOrderFromBackend();
        this.sortProjects();
        this.updatePagination();
        this.isLoading = false;
        this.projectsLoaded = true;

        this.fetchPiecesForAllProjects();
      },
      (error) => {
        console.error('Error fetching agency data:', error);
        this.isLoading = false;
        this.piecesLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de charger les projets.',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    );
  }

  private fetchPiecesForAllProjects(): void {
    let completedRequests = 0;
    const total = this.list.length;

    this.list.forEach(project => {
      this.projetService.getProjet(String(project.id)).subscribe({
        next: (fullProject: any) => {
          project.pieces = fullProject.pieces || [];
          completedRequests++;
          if (completedRequests === total) {
            this.piecesLoading = false;
          }
          if (this.sortBy === 'apartments') {
            this.sortProjects();
            this.updatePagination();
          }
        },
        error: (err) => {
          console.error(`Failed to fetch pieces for project ${project.id}:`, err);
          completedRequests++;
          if (completedRequests === total) {
            this.piecesLoading = false;
          }
        }
      });
    });
  }

  private initializeOrderFromBackend(): void {
    const hasAllDisplayOrders = this.list.every(p => p.displayOrder !== undefined && p.displayOrder !== null);
    if (hasAllDisplayOrders) {
      this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      this.list.sort((a, b) => {
        const dateA = this.parseDate(a.createdAt);
        const dateB = this.parseDate(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
    }
    this.list.forEach((project, index) => {
      project.displayOrder = index + 1;
    });
  }

  sortProjects(): void {
    switch (this.sortBy) {
      case 'name':
        this.list.sort((a, b) => {
          const nameA = a.name?.toLowerCase() || '';
          const nameB = b.name?.toLowerCase() || '';
          return this.sortDirection === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        });
        break;
      case 'date':
        this.list.sort((a, b) => {
          const dateA = this.parseDate(a.createdAt);
          const dateB = this.parseDate(b.createdAt);
          return this.sortDirection === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
        });
        break;
      case 'apartments':
        this.list.sort((a, b) => {
          const apartmentsA = a.pieces?.length || 0;
          const apartmentsB = b.pieces?.length || 0;
          return this.sortDirection === 'asc' ? apartmentsA - apartmentsB : apartmentsB - apartmentsA;
        });
        break;
      case 'order':
      default:
        this.list.sort((a, b) => {
          return this.sortDirection === 'asc'
            ? (a.displayOrder || 1) - (b.displayOrder || 1)
            : (b.displayOrder || 1) - (a.displayOrder || 1);
        });
        break;
    }
  }

  onSortChange(): void {
    this.sortProjects();
    this.updatePagination();
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.sortProjects();
    this.updatePagination();
  }

  get filteredProjects(): Project[] {
    if (!this.list) return [];
    let filtered = [...this.list];
    if (this.searchTerm) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(project =>
        project.name?.toLowerCase().includes(searchLower) ||
        project.description?.toLowerCase().includes(searchLower) ||
        project.adresse?.toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.filteredProjects.length / this.itemsPerPage);
    this.currentPage = Math.min(this.currentPage, this.totalPages || 1);
    this.updatePaginatedProjects();
  }

  updatePaginatedProjects(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedProjects = this.filteredProjects.slice(startIndex, endIndex);
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.updatePaginatedProjects();
    }
  }

  getPageNumbers(): number[] {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = startPage + maxVisiblePages - 1;
    if (endPage > this.totalPages) {
      endPage = this.totalPages;
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  moveProject(project: Project, direction: 'up' | 'down'): void {
    const currentIndex = this.list.findIndex(p => p.id === project.id);
    if (currentIndex === -1) return;

    let newIndex = currentIndex;
    if (direction === 'up') {
      newIndex = Math.max(0, currentIndex - 1);
    } else {
      newIndex = Math.min(this.list.length - 1, currentIndex + 1);
    }

    if (newIndex === currentIndex) return;

    const tempOrder = this.list[currentIndex].displayOrder;
    this.list[currentIndex].displayOrder = this.list[newIndex].displayOrder;
    this.list[newIndex].displayOrder = tempOrder;

    this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    this.list.forEach((p, idx) => {
      p.displayOrder = idx + 1;
    });

    this.updatePagination();
    this.saveAllProjectOrders();

    Swal.fire({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
      timerProgressBar: true,
      icon: 'success',
      title: 'Ordre modifié'
    });
  }

  private saveAllProjectOrders(): void {
    this.list.forEach(project => {
      if (project.displayOrder !== undefined) {
        this.projetService.updateProjetOrder(String(project.id), project.displayOrder).subscribe({
          next: () => console.log(`Order for project ${project.id} updated to ${project.displayOrder}`),
          error: (error) => console.error(`Error updating project order:`, error)
        });
      }
    });
  }

  // ========== DELETE WITH IMAGE REMOVAL ==========
  confirmDelete(project: Project): void {
    Swal.fire({
      title: "Supprimer ce projet ?",
      text: `Êtes-vous sûr de vouloir supprimer "${project.name}" ? Cette action est irréversible et supprimera également toutes les images associées.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteProjectWithImages(project);
      }
    });
  }

  private deleteProjectWithImages(project: Project): void {
    // Show loading
    Swal.fire({
      title: 'Suppression en cours...',
      text: 'Veuillez patienter',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    // Collect all image URLs from the project
    const imageUrls: string[] = [];
    if (project.photos && Array.isArray(project.photos)) {
      imageUrls.push(...project.photos.filter(url => url && url.trim() !== ''));
    }
    if (project.promoImg && project.promoImg.trim() !== '') {
      imageUrls.push(project.promoImg.trim());
    }

    // If there are images, delete them first
    if (imageUrls.length === 0) {
      // No images to delete, directly delete the project
      this.deleteProjectRecord(project);
      return;
    }

    // Delete all images from AWS
    const deletionPromises = imageUrls.map(url =>
      this.awsService.deleteFile(url).toPromise()
    );

    Promise.allSettled(deletionPromises)
      .then((results) => {
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          console.warn(`Failed to delete ${failed.length} image(s) from AWS. Continuing with project deletion.`);
        }
        // Now delete the project record
        this.deleteProjectRecord(project);
      })
      .catch((err) => {
        console.error('Error during image deletion:', err);
        // Still attempt to delete the project
        this.deleteProjectRecord(project);
      });
  }

  private deleteProjectRecord(project: Project): void {
    this.projetService.deleteProjet(String(project.id)).subscribe({
      next: () => {
        Swal.close();
        Swal.fire({
          title: "Supprimé !",
          text: "Le projet et ses images ont été supprimés avec succès.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
        // Reload projects to refresh the list
        this.loadProjects();
      },
      error: (error) => {
        Swal.close();
        console.error('Error deleting project:', error);
        Swal.fire({
          title: 'Erreur !',
          text: 'Impossible de supprimer le projet.',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    });
  }

  // ========== NAVIGATION ==========
  navigateToAddProject(): void {
    this.router.navigate(['/ajouter-projet']);
  }

  navigateToAddApartment(projectId: number): void {
    this.router.navigate(['/ajouter-pieces', String(projectId)]);
  }

  navigateToEditProject(projectId: number): void {
    this.router.navigate(['/modifier-projet', String(projectId)]);
  }

  navigateToApartments(projectId: number): void {
    this.router.navigate(['/list-appartement', String(projectId)]);
  }

  // UPDATED: Return to List-Promoteurs
  navigateBack(): void {
    this.router.navigate(['/List-Promoteurs']);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.updatePagination();
  }

  getTotalApartments(): number {
    return this.list.reduce((total, project) => total + (project.pieces?.length || 0), 0);
  }

  isMoveUpDisabled(project: Project): boolean {
    if (this.sortBy !== 'order') return true;
    const index = this.list.findIndex(p => p.id === project.id);
    return index <= 0;
  }

  isMoveDownDisabled(project: Project): boolean {
    if (this.sortBy !== 'order') return true;
    const index = this.list.findIndex(p => p.id === project.id);
    return index >= this.list.length - 1;
  }

  trackByProjectId(index: number, project: Project): number {
    return project.id;
  }
}